/**
 * The audio graph and its lifecycle, shared by the effects (sfx.ts) and the ambience bed
 * (ambience.ts). Procedural Web Audio only — no assets.
 *
 *   effect voices ─▶ fx bus ──┐
 *   ambience beds ─▶ amb bus ─┴─▶ master ─▶ soft compressor ─▶ speakers
 *
 * Autoplay policy: the AudioContext is created by `unlockAudio()`, which the caller must invoke
 * from a user gesture. Until then every sound is silently skipped. The context is suspended while
 * muted or while the tab is hidden, so an idle war room costs no CPU.
 */
import { settingsStore } from '../app/settings';

export interface Mixer {
  ctx: AudioContext;
  /** Interface and signal effects. */
  fx: GainNode;
  /** Room tone, wind, distant guns, bells. */
  amb: GainNode;
  /** 1 s of white noise (bursts, static). */
  white: AudioBuffer;
  /** 6 s of seamless brown noise (room tone, rumble). */
  brown: AudioBuffer;
}

let mixer: Mixer | null = null;
let master: GainNode | null = null;
const readyListeners = new Set<(m: Mixer) => void>();

/** Seconds for gain changes to settle — slider moves glide instead of clicking. */
const GLIDE = 0.08;

function whiteNoise(ctx: AudioContext, seconds: number): AudioBuffer {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

/** Brown (1/f²) noise, cross-faded at the seam so it loops without a click. */
function brownNoise(ctx: AudioContext, seconds: number): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < n; i++) {
    last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
    d[i] = last * 3.5;
  }
  const fade = Math.floor(ctx.sampleRate * 0.25);
  for (let i = 0; i < fade; i++) {
    const t = i / fade;
    d[n - fade + i] = d[n - fade + i] * (1 - t) + d[i] * t;
  }
  return buf;
}

/** True when sound would actually be heard (unlocked, not muted, volume up, tab visible). */
export function isAudible(): boolean {
  const s = settingsStore.getState();
  return (
    mixer !== null &&
    mixer.ctx.state === 'running' &&
    !s.muted &&
    s.volume > 0.001 &&
    typeof document !== 'undefined' &&
    !document.hidden
  );
}

/** The mixer once unlocked, else null. Never creates the context. */
export function getMixer(): Mixer | null {
  return mixer;
}

/** Run `fn` as soon as the mixer exists (immediately if it already does). */
export function onAudioReady(fn: (m: Mixer) => void): () => void {
  if (mixer) fn(mixer);
  else readyListeners.add(fn);
  return () => readyListeners.delete(fn);
}

function applySettings(): void {
  if (!mixer || !master) return;
  const s = settingsStore.getState();
  const t = mixer.ctx.currentTime;
  master.gain.setTargetAtTime(s.muted ? 0 : s.volume, t, GLIDE);
  mixer.fx.gain.setTargetAtTime(s.effectsVolume, t, GLIDE);
  mixer.amb.gain.setTargetAtTime(s.ambienceVolume, t, GLIDE);
  syncRunning();
}

/** Suspend the context while nobody can hear it; resume when they can. */
function syncRunning(): void {
  if (!mixer) return;
  const wanted = !settingsStore.getState().muted && !document.hidden;
  const { ctx } = mixer;
  if (wanted && ctx.state === 'suspended') void ctx.resume().catch(() => {});
  else if (!wanted && ctx.state === 'running') void ctx.suspend().catch(() => {});
}

/**
 * Create (or resume) the audio context. Call from a user gesture handler only. Returns null when
 * Web Audio is unavailable.
 */
export function unlockAudio(): Mixer | null {
  if (mixer) {
    syncRunning();
    return mixer;
  }
  let ctx: AudioContext;
  try {
    ctx = new AudioContext();
  } catch {
    return null;
  }
  master = ctx.createGain();
  master.gain.value = 0;
  // A gentle safety limiter: several cues landing at once must never spike.
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -20;
  comp.knee.value = 12;
  comp.ratio.value = 3;
  comp.attack.value = 0.01;
  comp.release.value = 0.3;
  master.connect(comp).connect(ctx.destination);
  const fx = ctx.createGain();
  const amb = ctx.createGain();
  fx.connect(master);
  amb.connect(master);
  mixer = { ctx, fx, amb, white: whiteNoise(ctx, 1), brown: brownNoise(ctx, 6) };
  applySettings();
  settingsStore.subscribe((s, p) => {
    if (
      s.volume !== p.volume ||
      s.muted !== p.muted ||
      s.effectsVolume !== p.effectsVolume ||
      s.ambienceVolume !== p.ambienceVolume
    )
      applySettings();
  });
  document.addEventListener('visibilitychange', syncRunning);
  const ready = mixer;
  for (const fn of readyListeners) fn(ready);
  readyListeners.clear();
  return mixer;
}
